import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import HomeNav, { useLanguageContext } from '../navigations/homenav';
import Footer from '../navigations/Footer';
import axios from 'axios';

const GalleryPage = () => {
  const { t } = useLanguageContext();
  const [galleryImages, setGalleryImages] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [expandedDescriptions, setExpandedDescriptions] = useState({});

  const imagesPerPage = 12;

  // Fetch images from backend
  useEffect(() => {
    axios
      .get("https://aaapd.org.et/gallery_backend/api/getImages.php")
      .then((response) => {
        setGalleryImages(response.data);
      })
      .catch((error) => {
        console.error("Error fetching gallery images:", error);
      });
  }, []);

  const openModal = (src) => {
    setSelectedImage(src);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setSelectedImage('');
  };

  const toggleDescription = (index) => {
    setExpandedDescriptions(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  // Pagination logic
  const indexOfLastImage = currentPage * imagesPerPage;
  const indexOfFirstImage = indexOfLastImage - imagesPerPage;
  const currentImages = galleryImages.slice(indexOfFirstImage, indexOfLastImage);

  const paginate = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo(0, 0);
  };

  return (
    <div className="bg-white text-gray-800">
      <HomeNav />

      <main id="main">
        {/* Hero Section */}
        <section
          className="relative bg-cover bg-center bg-no-repeat text-white"
          style={{
            backgroundImage: "url('/galleryimage3.jpg')",
            minHeight: '100vh',
            backgroundSize: 'cover',
            backgroundPosition: 'center center',
            backgroundRepeat: 'no-repeat'
          }}
        >
          <div className="absolute inset-0 bg-black bg-opacity-50"></div>
          <div className="relative max-w-7xl mx-auto px-6 text-center flex items-center justify-center min-h-screen">
            <div className="py-20">
              <h1 className="text-4xl md:text-5xl font-bold tracking-tight">{t('galleryPageTitle')}</h1>
              <p className="mt-4 text-lg text-gray-300">
                <Link to="/" className="hover:underline">{t('home')} /</Link> {t('gallery')}
              </p>
            </div>
          </div>
        </section>

        {/* Gallery Grid Section */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6">
            {Array.isArray(currentImages) && currentImages.length === 0 ? (
              <div className="text-center py-16">
                <div className="text-6xl mb-4">🖼️</div>
                <h3 className="text-2xl font-bold text-gray-800 mb-2">{t('galleryEmptyTitle')}</h3>
                <p className="text-gray-600">{t('galleryEmptyDesc')}</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-8">
                {currentImages.map((image, index) => (
                  <div 
                    key={index} 
                    className="group relative bg-white rounded-lg shadow-lg overflow-hidden cursor-pointer hover:shadow-xl transition-all duration-300"
                    onClick={() => openModal(image.src)}
                  >
                    <img 
                      src={image.src}
                      alt={image.alt}
                      className="w-full h-48 object-cover"
                    />
                    <div className="p-4">
                      {image.description ? (
                        <div className="text-gray-800 text-sm font-medium line-clamp-2 mb-2">
                          <p className={`${
                            expandedDescriptions[index] ? '' : 'line-clamp-3'
                          }`}>
                            {image.description}
                          </p>
                          {image.description.length > 150 && !expandedDescriptions[index] && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleDescription(index);
                              }}
                              className="text-teal-600 hover:text-teal-700 font-medium mt-1 text-xs"
                            >
                              {t('galleryReadMore')}
                            </button>
                          )}
                          {expandedDescriptions[index] && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleDescription(index);
                              }}
                              className="text-teal-600 hover:text-teal-700 font-medium mt-1 text-xs"
                            >
                              {t('galleryShowLess')}
                            </button>
                          )}
                        </div>
                      ) : (
                        <p className="text-gray-600 text-sm font-medium mb-2">{t('galleryImageFallback')}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Pagination */}
            <div className="mt-12 flex justify-center">
              <nav>
                <ul className="inline-flex items-center -space-x-px">
                  {Array.from({ length: Math.ceil(galleryImages.length / imagesPerPage) }, (_, i) => i + 1).map(number => (
                    <li key={number}>
                      <button
                        onClick={() => paginate(number)}
                        className={`py-2 px-4 leading-tight border ${
                          currentPage === number
                            ? 'bg-teal-700 text-white border-teal-700'
                            : 'bg-white text-gray-500 border-gray-300 hover:bg-gray-100 hover:text-gray-700'
                        }`}
                      >
                        {number}
                      </button>
                    </li>
                  ))}
                </ul>
              </nav>
            </div>
          </div>
        </section>
      </main>

      {/* Modal */}
      {modalOpen && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75 transition-opacity duration-300"
          onClick={closeModal}
        >
          <div className="relative max-w-3xl max-h-full p-4" onClick={(e) => e.stopPropagation()}>
            <img 
              src={selectedImage}
              alt={t('enlargedViewAlt')} 
              className="w-auto h-auto max-w-full max-h-[80vh] rounded-lg shadow-2xl"
            />
            <button 
              onClick={closeModal} 
              className="absolute top-4 right-4 text-red-500 text-4xl font-bold hover:text-gray-300 transition-colors"
            >
              &times;
            </button>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default GalleryPage;
